import { Topic } from '@/@types/topic'
import ExitIcon from '@/assets/exit.svg?react'
import StickStickIcon from '@/assets/stick-stick.svg?react'
import StickUnstickIcon from '@/assets/stick-unstick.svg?react'
import AutoLoadMoreIndicator from '@/components/AutoLoadMoreIndicator'
import { MenuItemData } from '@/components/DropdownMenu/types'
import BasicListItem from '@/components/List/ListItem'
import ListSection from '@/components/List/ListSection'
import { showConfirm } from '@/components/Modal/utils'
import MoreMenuButton from '@/components/MoreMenuButton'
import Page from '@/components/Page'
import {
	getSubscribedTopicsQueryOptions,
	getTopicsToppingQueryOptions,
} from '@/utils/fetchers/topic'
import {
	useChangeTopicSubscriptionStatusToggle,
	useToppingShortcutTopicToggle,
} from '@/utils/hooks/useTopicActionHandlers'
import useTopics from '@/utils/hooks/useTopics'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { HTMLMotionProps, LayoutGroup, motion } from 'motion/react'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'
import UserPageTabLoading from './-components/UserPageTabLoading'
import styles from './topic.module.css'

enum MoreAction {
	UNPIN = 'UNPIN',
	PIN = 'PIN',
	UNSUBSCRIBE = 'UNSUBSBRIBE',
}

interface ListItemProps extends HTMLMotionProps<'div'> {
	data: Topic
}
function ListItem({ data, className, ...props }: ListItemProps) {
	const moreMenuData: MenuItemData[] = useMemo(() => {
		const items: MenuItemData[] = []
		if (data.inShortcuts) {
			items.push({
				id: MoreAction.UNPIN,
				title: '取消置顶',
				icon: <StickUnstickIcon />,
			})
		} else {
			items.push({
				id: MoreAction.PIN,
				title: '置顶',
				icon: <StickStickIcon />,
			})
		}
		items.push({
			id: MoreAction.UNSUBSCRIBE,
			title: '退出圈子',
			icon: <ExitIcon />,
		})
		return items
	}, [data])
	const { toggleToppingShortcut } = useToppingShortcutTopicToggle(data.id)
	const { toggleSubscriptionStatus } = useChangeTopicSubscriptionStatusToggle(data.id)

	const handleMoreMenuSelect = async (actionId: string) => {
		// 根据不同的操作类型执行相应的逻辑
		switch (actionId) {
			case MoreAction.PIN:
				await toggleToppingShortcut()
				toast.success('已置顶')
				break
			case MoreAction.UNPIN:
				await toggleToppingShortcut()
				toast.success('已取消置顶')
				break

			case MoreAction.UNSUBSCRIBE:
				await showConfirm('确定退出本圈子吗？')
				await toggleSubscriptionStatus()
				toast.success('已退出圈子')
				break
			default:
				break
		}
	}

	return (
		<BasicListItem className={clsx(styles.listItem, className)} {...props}>
			<img src={data.squarePicture.thumbnailUrl} className={styles.image} />
			<div className={styles.content}>
				<div className={styles.title}>{data.content}</div>

				<div className={styles.briefIntro}>{data.briefIntro}</div>
			</div>
			<MoreMenuButton data={moreMenuData} onSelect={handleMoreMenuSelect} />
		</BasicListItem>
	)
}

export const Route = createFileRoute('/u/$username/topic')({
	component: RouteComponent,
	pendingComponent: UserPageTabLoading,
})

function RouteComponent() {
	const { username } = Route.useParams()
	const { data: toppingTopicIds } = useSuspenseQuery(getTopicsToppingQueryOptions())
	const { data, fetchNextPage, refetch, isFetchingNextPage } = useSuspenseInfiniteQuery(
		getSubscribedTopicsQueryOptions(username)
	)
	const topicIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const toppingTopics = useTopics(toppingTopicIds)
	const topics = useTopics(topicIds)

	const navigate = useUniversalNavigate()

	useTitle(`我的圈子 - 即刻`)

	return (
		<Page>
			<motion.div className={styles.container}>
				<LayoutGroup>
					<ListSection title="我置顶的圈子" layout="position">
						{toppingTopics.map((item) => (
							<ListItem
								layout="position"
								key={item.id}
								data={item}
								data-clickable-feedback
								onClick={(e) => {
									navigate(
										{
											to: '/topic/$id',
											params: {
												id: item.id,
											},
										},
										e
									)
								}}
							/>
						))}
					</ListSection>
					<ListSection title="我加入的圈子" layout="position">
						{topics.map((item) => (
							<ListItem
								layout="position"
								key={item.id}
								data={item}
								data-clickable-feedback
								onClick={() => {
									navigate({
										to: '/topic/$id',
										params: {
											id: item.id,
										},
									})
								}}
							/>
						))}
						<AutoLoadMoreIndicator
							isLoading={isFetchingNextPage}
							onLoadMore={async () => void (await fetchNextPage())}
						/>
					</ListSection>
				</LayoutGroup>
			</motion.div>
		</Page>
	)
}
